import { useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listSlotTemplates } from "@/api/catalog";
import {
  bulkApplyMyDesiderata,
  createMyDesideratum,
  deleteMyDesideratum,
  listMyDesiderata,
  listMyOpenWindows,
} from "@/api/desiderata";
import { listOrganizations } from "@/api/organizations";
import type {
  Desideratum,
  DesiderataKind,
  DesiderataWindow,
  SlotTemplate,
} from "@/api/types";
import { useAuthStore } from "@/store/authStore";
import { DesiderataGrid, type ApplyArgs } from "@/components/DesiderataGrid";
import { PageHeader } from "@/components/PageHeader";

const KIND_ICON: Record<DesiderataKind, React.ReactElement> = {
  WISH: <FavoriteIcon fontSize="small" />,
  AVOID: <ThumbDownAltOutlinedIcon fontSize="small" />,
  UNAVAILABLE: <EventBusyIcon fontSize="small" />,
  SWAP_OK: <SwapHorizIcon fontSize="small" />,
};

const KIND_COLOR: Record<DesiderataKind, "success" | "warning" | "error" | "info"> = {
  WISH: "success",
  AVOID: "warning",
  UNAVAILABLE: "error",
  SWAP_OK: "info",
};

const KINDS: DesiderataKind[] = ["WISH", "AVOID", "UNAVAILABLE", "SWAP_OK"];

function monthLabel(year: number, month: number, locale: string): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString(locale, { month: "long", year: "numeric" });
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function DesiderataPage() {
  const { t, i18n } = useTranslation();
  const me = useAuthStore((s) => s.me);

  const isPractitioner = Boolean(me?.id) && !me?.is_god;

  const windows = useQuery({
    queryKey: ["my-desiderata-windows"],
    queryFn: () => listMyOpenWindows(),
    enabled: isPractitioner,
  });

  const [selectedWindowId, setSelectedWindowId] = useState<string>("");

  // Pick the first window by default
  useMemo(() => {
    if (!selectedWindowId && windows.data?.length) {
      setSelectedWindowId(windows.data[0]?.id ?? "");
    }
  }, [windows.data, selectedWindowId]);

  const selectedWindow = windows.data?.find((w) => w.id === selectedWindowId) ?? null;

  if (!isPractitioner) {
    return (
      <Box>
        <PageHeader title={t("desiderata.title")} subtitle={t("desiderata.subtitle")} helpTerm="desiderata" />
        <Alert severity="info">{t("desiderata.needPractitionerLink")}</Alert>
      </Box>
    );
  }

  if (windows.isLoading) return <CircularProgress />;

  if ((windows.data ?? []).length === 0) {
    return (
      <Box>
        <PageHeader title={t("desiderata.title")} subtitle={t("desiderata.subtitle")} helpTerm="desiderata" />
        <Alert severity="info">{t("desiderata.noOpenWindow")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={t("desiderata.title")} subtitle={t("desiderata.subtitle")} helpTerm="desiderata" />

      <FormControl size="small" sx={{ minWidth: 320, mb: 2 }}>
        <InputLabel>Fenêtre</InputLabel>
        <Select
          label="Fenêtre"
          value={selectedWindowId}
          onChange={(e) => setSelectedWindowId(e.target.value)}
        >
          {(windows.data ?? []).map((w) => (
            <MenuItem key={w.id} value={w.id}>
              <WindowOptionLabel window={w} locale={i18n.language} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedWindow && <WindowEditor window={selectedWindow} />}
    </Box>
  );
}


function WindowOptionLabel({ window: w, locale }: { window: DesiderataWindow; locale: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ textTransform: "capitalize" }}>
      <Typography>{monthLabel(w.year, w.month, locale)}</Typography>
      <Chip size="small" variant="outlined" label={`${w.quota_points} pts`} />
    </Stack>
  );
}


function WindowEditor({ window: w }: { window: DesiderataWindow }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const desiderata = useQuery({
    queryKey: ["my-desiderata", w.id],
    queryFn: () => listMyDesiderata(w.id),
  });

  const orgs = useQuery({
    queryKey: ["organizations-all"],
    queryFn: () => listOrganizations({ limit: 100 }),
  });
  const orgName = orgs.data?.items.find((o) => o.id === w.organization_id)?.name ?? "";

  const slots = useQuery({
    queryKey: ["slot-templates", w.organization_id, true],
    queryFn: () => listSlotTemplates(w.organization_id, true),
  });

  const used = (desiderata.data ?? [])
    .filter((d) => d.kind !== "UNAVAILABLE")
    .reduce((sum, d) => sum + d.weight, 0);
  const usedPct = w.quota_points > 0 ? Math.min(100, (used / w.quota_points) * 100) : 0;

  const slotById = useMemo(() => {
    const m = new Map<string, SlotTemplate>();
    for (const s of slots.data ?? []) m.set(s.id, s);
    return m;
  }, [slots.data]);

  // Form state
  const [date, setDate] = useState(ymd(w.year, w.month, 1));
  const [kind, setKind] = useState<DesiderataKind>("WISH");
  const [weight, setWeight] = useState(3);
  const [slotId, setSlotId] = useState<string>("");
  const [comment, setComment] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const days = daysInMonth(w.year, w.month);

  const createMutation = useMutation({
    mutationFn: () =>
      createMyDesideratum(w.id, {
        date,
        slot_template_id: slotId || null,
        kind,
        weight,
        comment: comment || null,
      }),
    onSuccess: () => {
      setServerError(null);
      setComment("");
      qc.invalidateQueries({ queryKey: ["my-desiderata", w.id] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      if (detail && typeof detail === "object" && (detail as { error?: string }).error === "quota_exceeded") {
        setServerError(t("desiderata.quotaExceeded"));
      } else if (detail === "duplicate_desideratum") {
        setServerError(t("desiderata.duplicate"));
      } else if (detail === "window_not_open") {
        setServerError(t("desiderata.windowNotOpen"));
      } else {
        setServerError(typeof detail === "string" ? detail : (e as Error).message);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMyDesideratum(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-desiderata", w.id] }),
  });

  const bulkMutation = useMutation({
    mutationFn: (args: ApplyArgs) =>
      bulkApplyMyDesiderata(w.id, {
        cells: args.cells,
        kind: args.kind,
        weight: args.weight,
        replace_existing: true,
      }),
    onSuccess: () => {
      setServerError(null);
      qc.invalidateQueries({ queryKey: ["my-desiderata", w.id] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  const bulkClearMutation = useMutation({
    mutationFn: async (
      cells: { date: string; slot_template_id: string | null }[],
    ) => {
      const lookup = new Map<string, string>();
      for (const d of desiderata.data ?? []) {
        lookup.set(`${d.date}|${d.slot_template_id ?? ""}`, d.id);
      }
      for (const c of cells) {
        const id = lookup.get(`${c.date}|${c.slot_template_id ?? ""}`);
        if (id) await deleteMyDesideratum(id);
      }
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["my-desiderata", w.id] }),
  });

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
              {monthLabel(w.year, w.month, i18n.language)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orgName}
            </Typography>
            <Chip
              size="small"
              color={w.status === "OPEN" ? "success" : "default"}
              label={t(`desiderata.windowStatus.${w.status}`)}
            />
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t("desiderata.quotaUsed", { used, max: w.quota_points })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={usedPct}
              color={usedPct >= 100 ? "error" : usedPct >= 80 ? "warning" : "primary"}
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t("desiderata.gridTitle")}
          </Typography>
          <DesiderataGrid
            year={w.year}
            month={w.month}
            slots={slots.data ?? []}
            desiderata={desiderata.data ?? []}
            onApply={(args) => bulkMutation.mutate(args)}
            onClearCells={(cells) => bulkClearMutation.mutate(cells)}
            busy={bulkMutation.isPending || bulkClearMutation.isPending}
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t("desiderata.addDesideratum")}
          </Typography>
          {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t("desiderata.date")}</InputLabel>
                <Select
                  label={t("desiderata.date")}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                >
                  {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
                    <MenuItem key={d} value={ymd(w.year, w.month, d)}>
                      {pad2(d)}/{pad2(w.month)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("desiderata.slot")}</InputLabel>
                <Select
                  label={t("desiderata.slot")}
                  value={slotId}
                  onChange={(e) => setSlotId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t("desiderata.wholeDay")}</em>
                  </MenuItem>
                  {(slots.data ?? []).map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.label} ({s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as DesiderataKind)}
                >
                  {KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {KIND_ICON[k]}
                        <span>{t(`desiderata.kind.${k}`)}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label={t("desiderata.weight")}
                type="number"
                inputProps={{ min: 1, max: 5, step: 1 }}
                value={weight}
                onChange={(e) => setWeight(Math.min(5, Math.max(1, parseInt(e.target.value || "1", 10))))}
                sx={{ width: 110 }}
                helperText={kind === "UNAVAILABLE" ? "hors quota" : t("desiderata.weightHint")}
              />
            </Stack>
            <TextField
              size="small"
              label={t("desiderata.comment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending}
              >
                {t("common.save")}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {t("desiderata.submissions")}
          </Typography>
          {desiderata.isLoading ? (
            <CircularProgress size={20} />
          ) : (desiderata.data ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <List dense>
              {(desiderata.data ?? []).map((d) => (
                <DesideratumRow
                  key={d.id}
                  desideratum={d}
                  slotLabel={
                    d.slot_template_id
                      ? slotById.get(d.slot_template_id)?.label ?? "—"
                      : t("desiderata.wholeDay")
                  }
                  onDelete={() => {
                    if (confirm(t("desiderata.removeConfirm"))) deleteMutation.mutate(d.id);
                  }}
                />
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}


function DesideratumRow({
  desideratum: d,
  slotLabel,
  onDelete,
}: {
  desideratum: Desideratum;
  slotLabel: string;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ListItem
      divider
      secondaryAction={
        <Tooltip title={t("desiderata.removeConfirm")}>
          <IconButton edge="end" size="small" color="error" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
        <Chip
          size="small"
          color={KIND_COLOR[d.kind]}
          icon={KIND_ICON[d.kind]}
          label={t(`desiderata.kind.${d.kind}`)}
          variant="outlined"
        />
        {d.kind !== "UNAVAILABLE" && (
          <Chip size="small" variant="outlined" label={`${d.weight} pts`} />
        )}
      </Stack>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {d.date}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · {slotLabel}
            </Typography>
          </Stack>
        }
        secondary={d.comment ?? undefined}
      />
    </ListItem>
  );
}
